import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Github, Zap, Clock, Code, Terminal, Database, Shield } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen flex flex-col bg-dark-500">
      <Navbar />
      <main className="flex-grow pt-20">

        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-16">
            <span className="text-sm text-tasktide-teal font-medium">About TaskTide</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              Real Products. Real Code. Real Repos.
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
              TaskTide is not a design agency or a dev shop. It is an autonomous AI pipeline 
              that builds and ships working products every hour. Each one has a backend, a UI, 
              tests, and a private GitHub repo. You download the source and run it locally.
            </p>
          </div>

          {/* What we actually do */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
            <div className="glass-card p-8 rounded-xl border border-white/5">
              <div className="w-10 h-10 rounded-lg bg-tasktide-teal/10 flex items-center justify-center mb-5">
                <Clock className="h-5 w-5 text-tasktide-teal" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-3">Ship Every Hour</h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                An agent monitors a queue of product briefs, picks the next one, 
                generates a FastAPI backend, a React UI, and a pytest suite. 
                It commits to a private GitHub repo and tags a release. 
                No human writes the first commit.
              </p>
            </div>

            <div className="glass-card p-8 rounded-xl border border-white/5">
              <div className="w-10 h-10 rounded-lg bg-tasktide-purple/10 flex items-center justify-center mb-5">
                <Zap className="h-5 w-5 text-tasktide-purple" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-3">Local by Default</h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                Every product runs on your machine. No cloud inference subscriptions. 
                No data leaving your network. No API keys to manage. 
                Download, pip install, and run. You own the code and the data.
              </p>
            </div>

            <div className="glass-card p-8 rounded-xl border border-white/5">
              <div className="w-10 h-10 rounded-lg bg-tasktide-orange/10 flex items-center justify-center mb-5">
                <Github className="h-5 w-5 text-tasktide-orange" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-3">Open Source Ready</h2>
              <p className="text-gray-400 text-sm leading-relaxed">
                Every product is structured for open-source release. 
                Clean code, documented APIs, MIT license headers, 
                and a README that actually explains how to run it. 
                We are preparing select products for public release.
              </p>
            </div>
          </div>

          {/* Pipeline breakdown */}
          <div className="mb-20">
            <h2 className="text-2xl font-bold text-white mb-8">How the Pipeline Works</h2>
            <div className="space-y-4">
              {[
                { step: '1', icon: <Terminal className="h-4 w-4" />, title: 'Brief Ingest', desc: 'A product brief enters the queue: what it does, who it is for, and the core features.' },
                { step: '2', icon: <Code className="h-4 w-4" />, title: 'Backend Generation', desc: 'FastAPI scaffold with SQLModel ORM, Pydantic schemas, and route stubs. Typed end to end.' },
                { step: '3', icon: <Database className="h-4 w-4" />, title: 'Database + Migrations', desc: 'SQLite with Alembic migrations. Docker Compose for local Postgres if needed.' },
                { step: '4', icon: <Shield className="h-4 w-4" />, title: 'Test Suite', desc: 'pytest with in-memory DB, coverage reporting, and CI checks via GitHub Actions.' },
                { step: '5', icon: <Zap className="h-4 w-4" />, title: 'UI Scaffold', desc: 'React + Vite + Tailwind + shadcn/ui. Dark theme only. Mobile responsive.' },
                { step: '6', icon: <Github className="h-4 w-4" />, title: 'Commit + Tag', desc: 'Pushed to a private GitHub repo with semantic versioning. Ready to deploy or run locally.' }
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4 glass-card p-5 rounded-lg border border-white/5">
                  <div className="w-8 h-8 rounded-lg bg-dark-300 flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-tasktide-teal font-mono">{item.step}</span>
                      <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                    </div>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Founder */}
          <div className="glass-card p-8 rounded-xl border border-white/5 mb-16">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-tasktide-teal to-tasktide-purple flex items-center justify-center text-white text-xl font-bold shrink-0">
                MJ
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">Mani Jalali</h3>
                <p className="text-tasktide-teal text-sm mb-4">PhD Built Environment · UTS · Melbourne</p>
                <p className="text-gray-400 text-sm leading-relaxed max-w-2xl">
                  Built an autonomous AI pipeline that ships a product every hour. 
                  Background spans property technology, email automation systems, and autonomous AI agents. 
                  All 28 products are real repos with working code — no landing pages, no prototypes.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {['FastAPI', 'React', 'TypeScript', 'SQLModel', 'Docker', 'Pytest', 'Tailwind', 'LLMs'].map(tag => (
                    <span key={tag} className="px-2.5 py-1 rounded-md bg-dark-300/80 text-gray-300 text-xs border border-white/5">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-wrap gap-4">
            <Button asChild className="bg-gradient-to-r from-tasktide-teal to-tasktide-blue text-white">
              <Link to="/projects">
                Browse the Catalog <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-white/10 text-white hover:bg-dark-300/50">
              <Link to="/about/mission">Our Mission →</Link>
            </Button>
            <Button asChild variant="outline" className="border-white/10 text-white hover:bg-dark-300/50">
              <Link to="/about/technology">Our Technology →</Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;